import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { logger } from "../utils/logger";
import ValidProductDisplay from "./FullScreenValidProduct";
import MobileValidProduct from "./MobileValidProduct";
import UnverifiedProductDisplay from "./FullScreenUnverifiedProduct";
import NetworkErrorDisplay from "./FullScreenNetworkError";
import MobileNetworkError from "./MobileNetworkError";
import { animationKeyframes } from "./animations";
import MobileUnverifiedProduct from "./MobileUnverifiedProduct";
import ErrorBoundary from "./ErrorBoundary";
import { debounce } from "lodash";
import {
  getDeviceInfoHeadersSnapshot,
  getDeviceInfoMetricsSnapshot,
  startDeviceInfoCollection,
} from "../utils/deviceInfo";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  color: white;
  text-align: center;
  --glow-color: #0fa;
  --glow-duration: 0.52s;
  position: relative;
  background: white;
`;

const FadingLogo = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  animation: ${animationKeyframes.fadeInOut} 2s ease-in-out infinite;

  img {
    width: 180px;
    height: auto;
    filter: brightness(0);
  }
`;

const ProductVerification = () => {
  const { code } = useParams();
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isServerError, setIsServerError] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileView, setShowMobileView] = useState(false);
  const [showInitialAnimation, setShowInitialAnimation] = useState(false);

  const getNowMs = () => {
    if (typeof performance !== "undefined" && typeof performance.now === "function")
      return performance.now();
    return Date.now();
  };

  const newAttemptId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
      return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  // Show initial animation immediately on desktop
  useEffect(() => {
    if (!isMobile) {
      setShowInitialAnimation(true);
    }
  }, [isMobile]);

  const handleResize = () => {
    if (window.innerWidth < 768) {
      setShowMobileView(true);
      setIsMobile(true);
    } else {
      setShowMobileView(false);
      setIsMobile(false);
    }
  };

  const debouncedVerify = useCallback(
    debounce(
      async (code, scheduledAtMs, attemptId, deviceInfoPromise) => {
        const startedAtMs = getNowMs();
        const debounceWaitMs =
          typeof scheduledAtMs === "number"
            ? Math.max(0, Math.round(startedAtMs - scheduledAtMs))
            : null;

        let verifyApiMs = null;
        let verifyStatus = null;
        let verificationSucceeded = false;
        let apiStartMs = null;

        try {
          // Best-effort/partial device headers: verification must not wait on slow collectors.
          const deviceInfoHeaders = getDeviceInfoHeadersSnapshot();

          // Make API call with device info
          apiStartMs = getNowMs();
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/verify/${code}`,
            {
              headers: {
                ...deviceInfoHeaders,
                "x-user-agent": navigator.userAgent,
                "x-platform": navigator.platform,
                "x-language": navigator.language,
              },
            }
          );
          verifyApiMs = Math.round(getNowMs() - apiStartMs);
          verifyStatus = response.status;
          verificationSucceeded = Boolean(response.data?.success);

          if (response.status === 429) {
            throw new Error("Please wait a few seconds before verifying again.");
          }
          if (!response.data.success) {
            throw new Error("Verification failed");
          }
          setVerificationResult(response.data);
          setTimeout(() => {
            setShowAnimation(true);
          }, 100);
        } catch (error) {
          if (apiStartMs !== null && verifyApiMs === null) {
            verifyApiMs = Math.round(getNowMs() - apiStartMs);
          }

          if (error.response) {
            verifyStatus = error.response.status;
            const responseMessage = error.response.data?.message;

            // Handle rate limiting
            if (verifyStatus === 429) {
              setError("Please wait a few seconds before verifying again.");
            } else if (
              // Only set server error for actual server issues (500, 503)
              verifyStatus === 503 ||
              verifyStatus === 500 ||
              (responseMessage &&
                (responseMessage.toLowerCase().includes("database") ||
                  responseMessage.toLowerCase().includes("initialization")))
            ) {
              setIsServerError(true);
              setError("Server unavailable");
            } else {
              // For 404 or other client errors, treat as invalid product
              setVerificationResult({
                success: false,
                data: {
                  isValid: false,
                  code: code,
                  productName: "Unknown Product",
                  firstScanDate: new Date().toISOString(),
                  scanCount: 0,
                },
              });
              setTimeout(() => {
                setShowAnimation(true);
              }, 100);
            }
          } else if (error.request) {
            // Network error - no response received
            setIsServerError(true);
            setError("Server unavailable");
          } else {
            setIsServerError(true);
            setError("Server connection error");
          }

          logger.error("Verification error", {
            attemptId,
            status: error.response?.status,
            message: error.message,
          });
        } finally {
          setLoading(false);

          // Emit one timing event per attempt after device info collection completes (async; do not block UI).
          (async () => {
            const deviceInfoResult = await deviceInfoPromise?.catch(() => null);
            const metrics =
              deviceInfoResult?.metrics || getDeviceInfoMetricsSnapshot();

            logger.info("Verification latency breakdown", {
              event: "verification_latency_breakdown",
              attemptId,
              debounce_wait_ms: debounceWaitMs,
              verify_api_ms: verifyApiMs,
              verify_status: verifyStatus,
              verify_success: verificationSucceeded,
              ...metrics,
            });
          })().catch(() => {});
        }
      },
      2000,
      {
        // Leading edge for immediate initial verification; trailing edge for the
        // final code if it changes repeatedly within the 2s window.
        leading: true,
        trailing: true,
      }
    ),
    []
  );

  const handleVerify = async () => {
    const attemptId = newAttemptId();
    try {
      setLoading(true);
      setError(null);
      setVerificationResult(null);

      const deviceInfoPromise = startDeviceInfoCollection();
      const deviceInfoHeaders = getDeviceInfoHeadersSnapshot();

      const apiStartMs = getNowMs();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/verify/${code}`,
        {
          headers: {
            ...deviceInfoHeaders,
            "x-user-agent": navigator.userAgent,
            "x-platform": navigator.platform,
            "x-language": navigator.language,
          },
        }
      );
      const verifyApiMs = Math.round(getNowMs() - apiStartMs);

      setVerificationResult(response.data);

      (async () => {
        const deviceInfoResult = await deviceInfoPromise?.catch(() => null);
        const metrics = deviceInfoResult?.metrics || getDeviceInfoMetricsSnapshot();
        logger.info("Verification latency breakdown", {
          event: "verification_latency_breakdown",
          attemptId,
          debounce_wait_ms: 0,
          verify_api_ms: verifyApiMs,
          verify_status: response.status,
          verify_success: Boolean(response.data?.success),
          ...metrics,
        });
      })().catch(() => {});
    } catch (err) {
      setError(
        err.response?.data?.message || "An error occurred during verification"
      );
      logger.error("Verification error", {
        attemptId,
        status: err.response?.status,
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const verifyProduct = async () => {
      setLoading(true);
      setError(null);
      setIsServerError(false);
      setShowAnimation(false);

      // Special case handling for hardcoded product codes (no third-party collectors).
      if (code === "ShowMeValidProduct") {
        const mockData = {
          productName: "Bar Juice 5000",
          firstScanDate: new Date().toISOString(),
          scanCount: 1,
          code: "ShowMeValidProduct",
          isValid: true,
        };

        setVerificationResult({
          success: true,
          data: mockData,
        });
        setTimeout(() => {
          setShowAnimation(true);
        }, 100);
        setLoading(false);
        return;
      }

      if (code === "ShowMeInvalidProduct") {
        const mockData = {
          productName: "Bar Juice 5000",
          firstScanDate: new Date().toISOString(),
          scanCount: 1,
          code: "ShowMeInvalidProduct",
          isValid: false,
        };

        setVerificationResult({
          success: false,
          data: mockData,
        });
        setTimeout(() => {
          setShowAnimation(true);
        }, 100);
        setLoading(false);
        return;
      }

      if (code === "ShowMeNetworkError") {
        setVerificationResult({
          success: false,
          data: {
            code: "ShowMeNetworkError",
            isNetworkError: true,
            scanCount: 1,
            firstScanDate: new Date().toISOString(),
            lastScanDate: new Date().toISOString(),
          },
        });
        setIsServerError(true);
        setError("Server unavailable");
        setTimeout(() => {
          setShowAnimation(true);
        }, 100);
        setLoading(false);
        return;
      }

      // Start device info collection in the background to overlap with debounce.
      const attemptId = newAttemptId();
      const scheduledAtMs = getNowMs();
      const deviceInfoPromise = startDeviceInfoCollection();

      debouncedVerify(code, scheduledAtMs, attemptId, deviceInfoPromise);
    };

    if (code) {
      verifyProduct();
    } else {
      setLoading(false);
      setError("No verification code provided");
    }
    return () => {
      if (typeof debouncedVerify.cancel === "function") debouncedVerify.cancel();
    };
  }, [code, debouncedVerify]);

  if (loading) {
    return (
      <Container>
        <Helmet>
          <title>Product Verification - Bar Juice</title>
          <meta
            name="description"
            content="Verify the authenticity of your Bar Juice product"
          />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <FadingLogo>
          <img src="/images/bj-butterfly.png" alt="Bar Juice Butterfly Logo" />
        </FadingLogo>
      </Container>
    );
  }

  // Show network error display for server errors or network errors
  if ((isServerError && error) || verificationResult?.data?.isNetworkError) {
    return isMobile ? (
      <ErrorBoundary>
        <MobileNetworkError />
      </ErrorBoundary>
    ) : (
      <NetworkErrorDisplay
        verificationResult={
          verificationResult || { data: { isNetworkError: true, code } }
        }
      />
    );
  }

  // Check if the product is valid based on the response
  if (verificationResult?.data?.isValid) {
    if (isMobile) {
      return (
        <ErrorBoundary>
          <MobileValidProduct verificationResult={verificationResult} />
        </ErrorBoundary>
      );
    } else {
      return <ValidProductDisplay verificationResult={verificationResult} />;
    }
  }

  // Handle unverified product case
  if (isMobile) {
    return (
      <ErrorBoundary>
        <MobileUnverifiedProduct verificationResult={verificationResult} />
      </ErrorBoundary>
    );
  }

  return (
    <Container>
      <Helmet>
        <title>Product Verification - Bar Juice</title>
        <meta
          name="description"
          content="Verify the authenticity of your Bar Juice product"
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {showAnimation && (
        <UnverifiedProductDisplay
          verificationResult={
            verificationResult || { data: { isValid: false, code } }
          }
        />
      )}
    </Container>
  );
};

export default ProductVerification;
